/* ============================================================
   Dathent — shared plan definitions + Stripe checkout helper
   Single source of truth for the billing plan cards. Moved out of
   Settings.jsx so the focused UpgradeModal can reuse the EXACT same
   plan data and checkout call without duplicating it. Settings.jsx
   imports everything from here — its billing logic is unchanged.
   ============================================================ */
import { api } from '../services/api/client.js';

// The checkout worker's TRIAL_DAYS default is 7. It is not exposed by the
// billing catalog yet, so keep the client copy centralized here and update it
// alongside that backend default if the contract changes.
export const TRIAL_DAYS = 7;

// Map frontend plan card id <-> backend planKey
export const PLAN_KEY_BY_ID = {
  pilot: 'brand_pilot',
  operator: 'marketing_operator',
  multi: 'multi_brand',
  enterprise: 'enterprise',
};
export const PLAN_ID_BY_KEY = Object.fromEntries(Object.entries(PLAN_KEY_BY_ID).map(([id, key]) => [key, id]));
// Plans that are sold via sales contact (no self-service Stripe checkout)
export const SALES_CONTACT_PLANS = new Set(['operator', 'multi', 'enterprise']);

export const PLAN_DEFS = [
  {
    id: 'pilot', tier: 'Plan 01', name: 'Brand Pilot',
    desc: 'A working operation for one brand. Onboard your business, prepare your first content direction, ship your first calendar.',
    // Pricing reflects what Stripe ACTUALLY charges today (see
    // dathent-stripe-worker/src/stripe-brand-pilot-checkout.ts): a MONTHLY
    // €300 subscription (STRIPE_PRICE_BRAND_PILOT_MONTHLY), with a 50%
    // founding coupon (first_3_50) applied to a new customer's first 3
    // months → €150/mo for 3 months, then €300/mo. The earlier
    // "€300 / quarter" copy did NOT match the monthly Stripe price and was
    // corrected. When the Stripe price is switched to quarterly, update
    // these fields (and the landing) in one pass.
    featured: true,
    price: '€150', priceWas: '€300', priceUnit: '/mo',
    priceBadge: '−50% · first 3 months',
    priceThen: 'then €300/mo',
    priceTag: 'Founding rate',
    cta: 'Subscribe & unlock',
    feats: [
      'One brand, fully onboarded',
      'Deep market research — 3× more sources than free',
      'Strategy & content calendar, built for you',
      'Up to 6 social channels (Instagram, Facebook, X, LinkedIn, Threads, Telegram)',
      'Posts & visuals, platform-ready',
      'Brand voice, logo & colors applied',
      'Notifications & full visibility',
    ],
  },
  // Brand Pilot is the only sellable plan today; the higher tiers are the
  // roadmap (soon), each with its headline upgrades + a placeholder price line.
  {
    id: 'growth', soon: true, tier: 'Plan 02', name: 'Growth',
    desc: 'Scale past one brand — unlimited channels, every format.',
    soonPrice: 'Unlimited channels', soonNote: 'Pricing at launch',
    feats: [
      'Unlimited channels — any type',
      'Native TikTok slides',
      'Multi-brand',
      'More generations',
      'Advanced analytics',
      'Advanced support',
    ],
  },
  {
    id: 'studio', soon: true, tier: 'Plan 03', name: 'Studio',
    desc: 'More brands, deeper analysis, and dedicated horsepower behind each one.',
    soonPrice: 'Multiple brands', soonNote: 'Pricing at launch',
    feats: [
      'Everything in Growth',
      'More brands',
      'Priority support',
      'Better orchestration — more dedicated resources per brand',
      'Deeper analysis & brand promotion',
      'Advanced brand promotion',
    ],
  },
  {
    id: 'enterprise', soon: true, contact: true, tier: 'Plan 04', name: 'Enterprise',
    desc: 'Custom scope for larger operations — bespoke limits, integrations, and support.',
    customPrice: 'Custom', customNote: 'Tailored to your scope',
    cta: 'Contact sales',
    feats: [
      'Custom e-commerce solutions',
      'Huge limits on any content',
      'Dedicated support',
      'Tailored onboarding',
    ],
  },
];

// ============================================================
// NEW tariff grid (2026-07 rework, docs/VYTIAG-tarify-core-video-2026-07-06.md
// §2 / docs/TARIFF-REWORK-BRIEF.md). Deploy-dark: this array is inert until
// usePlanCatalog's `newGridActive` gate flips true (the DB catalog exposes
// 'starter' + 'growth' as public self-service rows — see hooks/usePlanCatalog.js).
//
// DELIBERATELY KEPT SEPARATE from PLAN_DEFS / PLAN_KEY_BY_ID / PLAN_ID_BY_KEY
// above rather than merged into them. Reason: the legacy PLAN_DEFS already has
// a "soon" placeholder card with id 'growth' (Plan 02 — an unpriced roadmap
// teaser, unrelated to this grid) that is NOT part of PLAN_KEY_BY_ID today.
// The new grid's real, sellable 'growth' plan (€49, backend planKey 'growth')
// is a completely different offering. If both lived in one id<->key map (or
// one PLAN_DEFS array), any shared lookup keyed by the STRING 'growth'
// (e.g. usePlanCatalog's findPlanDef, which checks `PLAN_DEFS.some(p => p.id
// === idOrKey)` first) would silently resolve to the WRONG "Growth" card.
// Keeping NEW_PLAN_DEFS / NEW_PLAN_KEY_BY_ID / NEW_PLAN_ID_BY_KEY as their own
// namespace — consulted ONLY by new-grid-aware code, itself gated by
// `newGridActive` — makes that collision structurally impossible instead of
// relying on call-site discipline. Same reasoning applies one level down: the
// i18n key names below use a `planGrowthV2*` prefix (not `planGrowth*`, which
// the legacy soon-card's translations already own in common.json).
//
// For the new grid the frontend card id IS the backend planKey — no id<->key
// indirection needed (unlike legacy pilot/operator/multi/enterprise ids,
// which predate this convention).
export const NEW_PLAN_DEFS = [
  {
    id: 'starter', tier: 'Plan 01', name: 'Starter',
    desc: 'Post yourself, AI helps. One brand, three channels, unlimited scheduling — AI steps in for the extra reach.',
    price: '€19', priceUnit: '/mo',
    cta: 'Subscribe & unlock',
    feats: [
      '1 brand, 3 channels',
      'Unlimited scheduled posts — post yourself, anytime',
      '75 AI posts (with visuals) / month',
      '9 AI carousels / month',
      // HONESTY: starter's entitlements carry research:false — the same teaser
      // preview as free, NOT a real (even "lite") research run. "Preview" is
      // the honest word; don't upgrade this to "lite"/"basic" unless starter
      // actually gets a metered research run.
      'Market research preview',
      '100 AI assistant turns / month',
      '2 GB media storage',
    ],
  },
  {
    id: 'growth', featured: true, tier: 'Plan 02', name: 'Growth',
    desc: 'Your SMM department on autopilot — every format, every channel, on schedule, with no one to hire.',
    price: '€49', priceUnit: '/mo',
    cta: 'Subscribe & unlock',
    feats: [
      '1 brand, 6 channels',
      'Unlimited scheduled posts',
      '240 AI posts (with visuals) / month',
      '30 AI carousels / month',
      'Autopilot posting',
      'Smart insights — feedback loop on every post',
      'Deep research ×1 / month',
      '300 AI assistant turns / month',
      '10 GB media storage',
    ],
  },
  {
    id: 'pro', tier: 'Plan 03', name: 'Pro',
    desc: 'Multi-brand — up to 3 brands, deeper research and higher limits for teams that need to scale.',
    price: '€99', priceUnit: '/mo',
    cta: 'Subscribe & unlock',
    feats: [
      '3 brands, 12 channels',
      'Unlimited scheduled posts',
      '500 AI posts (with visuals) / month',
      '60 AI carousels / month',
      'Autopilot posting, per brand',
      'Deep research ×3 / month',
      '600 AI assistant turns / month',
      '50 GB media storage',
    ],
  },
  {
    id: 'agency', tier: 'Plan 04', name: 'Agency',
    desc: 'An agency in one subscription — 10 brands, 30 channels, the highest limits.',
    price: '€249', priceUnit: '/mo',
    cta: 'Subscribe & unlock',
    feats: [
      '10 brands, 30 channels',
      'Unlimited scheduled posts',
      '1200 AI posts (with visuals) / month',
      '120 AI carousels / month',
      'Autopilot posting, per brand',
      'Deep research ×6 / month',
      '2000 AI assistant turns / month',
      '200 GB media storage',
    ],
  },
];

// Identity map: the new grid's frontend card id IS the backend planKey (no
// legacy-style indirection). Kept as its own export — symmetrical with
// PLAN_KEY_BY_ID/PLAN_ID_BY_KEY above but namespaced apart, per the collision
// note above. Do NOT fold these into PLAN_KEY_BY_ID/PLAN_ID_BY_KEY.
export const NEW_PLAN_KEY_BY_ID = Object.fromEntries(NEW_PLAN_DEFS.map((p) => [p.id, p.id]));
export const NEW_PLAN_ID_BY_KEY = NEW_PLAN_KEY_BY_ID;

// i18n key suffixes for the new grid's translatable display fields — mirrors
// PLAN_TEXT_KEYS's shape below. The 'growth' entry here uses a `planGrowthV2*`
// prefix (NOT `planGrowth*`) so it can never collide with the legacy soon-card
// translations of the same name already in common.json (see note above).
export const NEW_PLAN_TEXT_KEYS = {
  starter: {
    tier: 'planStarterTier', name: 'planStarterName', desc: 'planStarterDesc', cta: 'planStarterCta',
    priceUnit: 'planPriceUnit',
    feats: ['planStarterFeat1', 'planStarterFeat2', 'planStarterFeat3', 'planStarterFeat4', 'planStarterFeat6', 'planStarterFeat7', 'planStarterFeat8'],
  },
  growth: {
    tier: 'planGrowthV2Tier', name: 'planGrowthV2Name', desc: 'planGrowthV2Desc', cta: 'planGrowthV2Cta',
    priceUnit: 'planPriceUnit',
    feats: ['planGrowthV2Feat1', 'planGrowthV2Feat2', 'planGrowthV2Feat3', 'planGrowthV2Feat4', 'planGrowthV2Feat6', 'planGrowthV2Feat7', 'planGrowthV2Feat8', 'planGrowthV2Feat9', 'planGrowthV2Feat10'],
  },
  pro: {
    tier: 'planProTier', name: 'planProName', desc: 'planProDesc', cta: 'planProCta',
    priceUnit: 'planPriceUnit',
    feats: ['planProFeat1', 'planProFeat2', 'planProFeat3', 'planProFeat4', 'planProFeat7', 'planProFeat8', 'planProFeat9', 'planProFeat10'],
  },
  agency: {
    tier: 'planAgencyTier', name: 'planAgencyName', desc: 'planAgencyDesc', cta: 'planAgencyCta',
    priceUnit: 'planPriceUnit',
    feats: ['planAgencyFeat1', 'planAgencyFeat2', 'planAgencyFeat3', 'planAgencyFeat4', 'planAgencyFeat7', 'planAgencyFeat8', 'planAgencyFeat9', 'planAgencyFeat10'],
  },
};

// i18n: per-plan key suffixes for the translatable display fields. The raw
// English on PLAN_DEFS above is kept as a stable reference for logic
// (e.g. matching backend `entitlements.planName`); rendering uses t().
// These keys live under the `settings.*` namespace in common.json.
export const PLAN_TEXT_KEYS = {
  pilot: {
    tier: 'planPilotTier', name: 'planPilotName', desc: 'planPilotDesc', cta: 'planPilotCta',
    // Price NUMBERS (price / priceWas) are language-neutral and stay on
    // PLAN_DEFS; only the words are translated.
    priceUnit: 'planPriceUnit', priceBadge: 'planPilotPriceBadge', priceThen: 'planPilotPriceThen', priceTag: 'planPilotPriceTag',
    feats: ['planPilotFeat1', 'planPilotFeat2', 'planPilotFeat3', 'planPilotFeat4', 'planPilotFeat5', 'planPilotFeat6', 'planPilotFeat7'],
  },
  // Tier + name for growth/studio/enterprise come straight from PLAN_DEFS
  // (language-neutral: "Plan 02" / "Growth" etc.); only desc + price labels +
  // feats are translated.
  growth: {
    desc: 'planGrowthDesc', soonPrice: 'planGrowthSoonPrice', soonNote: 'planSoonPricingNote',
    feats: ['planGrowthFeat1', 'planGrowthFeat2', 'planGrowthFeat3', 'planGrowthFeat4', 'planGrowthFeat5', 'planGrowthFeat6'],
  },
  studio: {
    desc: 'planStudioDesc', soonPrice: 'planStudioSoonPrice', soonNote: 'planSoonPricingNote',
    feats: ['planStudioFeat1', 'planStudioFeat2', 'planStudioFeat3', 'planStudioFeat4', 'planStudioFeat5', 'planStudioFeat6'],
  },
  enterprise: {
    desc: 'planEnterpriseDesc', cta: 'planEnterpriseCta',
    customPrice: 'planEnterpriseCustomPrice', customNote: 'planEnterpriseCustomNote',
    feats: ['planEnterpriseSoonFeat1', 'planEnterpriseSoonFeat2', 'planEnterpriseSoonFeat3', 'planEnterpriseSoonFeat4'],
  },
};

/**
 * Start a self-service Stripe checkout for the given backend planKey.
 *
 * Existing callers return to /app/settings Billing. Trial-signup callers can
 * return to their current SPA route; `{CHECKOUT_SESSION_ID}` stays literal so
 * Stripe can inject the session id for post-checkout sync / PostPayWelcome.
 *
 * Throws if the API returns no checkout URL. Redirects on success.
 */
export async function startCheckout(planKey, { trialSignup, returnTo } = {}) {
  const origin = window.location.origin;
  const capturedReturnTo = `${window.location.pathname}${window.location.search}`;
  const returnUrl = new URL(returnTo || (trialSignup ? capturedReturnTo : '/app/settings?tab=billing'), origin);
  // Hosted returns must stay inside the SPA. A malformed/external caller value
  // falls back to Billing rather than letting Stripe redirect off-site.
  if (returnUrl.origin !== origin || (returnUrl.pathname !== '/app' && !returnUrl.pathname.startsWith('/app/'))) {
    returnUrl.href = `${origin}/app/settings?tab=billing`;
  }
  returnUrl.searchParams.delete('checkout');
  returnUrl.searchParams.delete('session_id');
  const successUrl = new URL(returnUrl);
  successUrl.searchParams.set('checkout', 'success');
  successUrl.searchParams.set('session_id', '{CHECKOUT_SESSION_ID}');
  const cancelUrl = new URL(returnUrl);
  cancelUrl.searchParams.set('checkout', 'cancelled');
  const res = await api.billing.createCheckoutSession({
    planKey,
    // URLSearchParams escapes braces; Stripe requires this exact literal token.
    successUrl: successUrl.toString().replace('%7BCHECKOUT_SESSION_ID%7D', '{CHECKOUT_SESSION_ID}'),
    cancelUrl: cancelUrl.toString(),
    trialSignup,
  });
  // PAY-NOW-DURING-TRIAL: the backend acted on the EXISTING subscription instead
  // of opening a new Stripe Checkout (no URL is returned). Only a genuinely
  // CHARGED conversion (status active) routes to the success return so PostPayWelcome
  // + the post-pay sync run as after a hosted checkout (the trial→active webhook
  // already kicked the deep research). A declined card (chargeFailed), an already
  // active sub, or a trial with no card go to billing settings WITHOUT a success
  // claim — the billing tab reflects the real (e.g. past_due) state to fix.
  const data = res?.data ?? res;
  if (data?.converted) {
    window.location.assign(`${origin}/app/settings?tab=billing&checkout=success`);
    return;
  }
  // TASK-331 (M3 fix): `alreadySubscribed` used to fall into the same silent
  // redirect as chargeFailed/needsCard below — a real dead end for the renew
  // flow (UpgradeModal's 'renew' variant calling startCheckout(previousPlanKey)
  // on a still-live Stripe subscription lands exactly here). Throw instead so
  // the caller's existing `catch (err) { setError(err?.message || ...) }`
  // surfaces a real, visible message. `.code` lets a caller show the
  // localized `upgradeModal.alreadySubscribed` copy; the plain-English
  // `.message` is a safe fallback for any caller that doesn't special-case it.
  if (data?.alreadySubscribed) {
    const err = new Error('Already subscribed to an active plan.');
    err.code = 'ALREADY_SUBSCRIBED';
    throw err;
  }
  if (data?.chargeFailed || data?.needsCard) {
    window.location.assign(`${origin}/app/settings?tab=billing`);
    return;
  }
  const url = res?.checkoutUrl || res?.url || res?.data?.checkoutUrl;
  if (!url) throw new Error('No checkout URL returned');
  window.location.assign(url);
}
